import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router';
import {
  Users,
  Mail,
  Globe,
  Plus,
  Trash2,
  RefreshCw,
  AlertCircle,
  Loader2,
  Building2,
  Shield,
  User,
  UserCog,
  UserMinus,
  UserPlus,
  Package,
} from 'lucide-react';
import { Modal } from '../components/ui';
import { useUserContext } from '../context/UserContext';
import {
  getMyVendorOrganization,
  getMyVendorUsers,
  getMyVendorInvitations,
  getMyVendorDomains,
  updateMyVendorUserRole,
  reactivateMyVendorUser,
  deactivateMyVendorUser,
  inviteToMyVendor,
  resendMyVendorInvitation,
  cancelMyVendorInvitation,
  addMyVendorDomain,
  removeMyVendorDomain,
  type VendorUser,
  type Invitation,
  type DomainWhitelist,
  type Vendor,
  type VendorUserRole,
} from '../api';
import { VendorProductsTab } from '../components/VendorProductsTab';

// Generic domains that should be blocked from whitelisting
const BLOCKED_DOMAINS = [
  'gmail.com',
  'googlemail.com',
  'hotmail.com',
  'outlook.com',
  'live.com',
  'msn.com',
  'yahoo.com',
  'yahoo.co.uk',
  'ymail.com',
  'aol.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'protonmail.com',
  'proton.me',
  'zoho.com',
  'mail.com',
  'gmx.com',
  'gmx.net',
  'fastmail.com',
  'tutanota.com',
  'hey.com',
];

type TabKey = 'users' | 'invitations' | 'domains' | 'products';
const VALID_TABS: TabKey[] = ['users', 'invitations', 'domains', 'products'];

export function VendorAdminPage() {
  const { user, brandId } = useUserContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get active tab from URL, default to 'users'
  const tabParam = searchParams.get('tab');
  const activeTab: TabKey = VALID_TABS.includes(tabParam as TabKey)
    ? (tabParam as TabKey)
    : 'users';

  const setActiveTab = (tab: TabKey) => {
    setSearchParams({ tab });
  };

  const fetchVendor = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getMyVendorOrganization();
      if (response.status) {
        setVendor(response.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load organization');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVendor();
  }, [fetchVendor]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !vendor) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle className="w-4 h-4" />
          {error || 'Failed to load organization details'}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-gray-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">{vendor.name}</h2>
              <p className="text-sm text-gray-500">Team Management</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
              <Shield className="w-4 h-4" />
              Admin
            </span>
          </div>
        </div>
        {user?.email && (
          <p className="mt-2 text-sm text-gray-500">
            Logged in as {user.email}
          </p>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 bg-gray-50 overflow-x-auto scrollbar-thin">
        <nav className="flex -mb-px px-4 min-w-0">
          <TabButton
            active={activeTab === 'users'}
            onClick={() => setActiveTab('users')}
            icon={<Users className="w-4 h-4" />}
            label="Team Members"
            testId="vendor-admin-tab-users"
          />
          <TabButton
            active={activeTab === 'invitations'}
            onClick={() => setActiveTab('invitations')}
            icon={<Mail className="w-4 h-4" />}
            label="Invitations"
            testId="vendor-admin-tab-invitations"
          />
          <TabButton
            active={activeTab === 'domains'}
            onClick={() => setActiveTab('domains')}
            icon={<Globe className="w-4 h-4" />}
            label="Domains"
            testId="vendor-admin-tab-domains"
          />
          <TabButton
            active={activeTab === 'products'}
            onClick={() => setActiveTab('products')}
            icon={<Package className="w-4 h-4" />}
            label="Products"
            testId="vendor-admin-tab-products"
          />
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-4 sm:p-6">
        {activeTab === 'users' && <UsersTab currentUserEmail={user?.email} />}
        {activeTab === 'invitations' && <InvitationsTab vendorName={vendor.name} />}
        {activeTab === 'domains' && <DomainsTab vendorName={vendor.name} />}
        {activeTab === 'products' && <VendorProductsTab brandId={brandId} />}
      </div>
    </div>
  );
}

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  testId?: string;
}

function TabButton({ active, onClick, icon, label, testId }: TabButtonProps) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
        active
          ? 'border-gray-900 text-gray-900'
          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

// ===== Users Tab =====
interface UsersTabProps {
  currentUserEmail?: string;
}

function UsersTab({ currentUserEmail }: UsersTabProps) {
  const [users, setUsers] = useState<VendorUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getMyVendorUsers();
      if (response.status) {
        setUsers(response.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleToggleRole = async (email: string, currentRole: VendorUserRole) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    const action = newRole === 'admin' ? 'promote' : 'demote';

    if (!confirm(`Are you sure you want to ${action} ${email} to ${newRole}?`)) return;

    try {
      setActionError(null);
      await updateMyVendorUserRole(email, newRole);
      setActionSuccess(`User ${action}d to ${newRole} successfully`);
      fetchUsers();
      setTimeout(() => setActionSuccess(null), 3000);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : `Failed to ${action} user`);
    }
  };

  const handleDeactivate = async (email: string) => {
    if (!confirm(`Are you sure you want to deactivate ${email}? They will no longer be able to access the system.`)) return;
    try {
      setActionError(null);
      await deactivateMyVendorUser(email);
      setActionSuccess('User deactivated successfully');
      fetchUsers();
      setTimeout(() => setActionSuccess(null), 3000);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to deactivate user');
    }
  };

  const handleReactivate = async (email: string) => {
    if (!confirm(`Reactivate ${email}?`)) return;
    try {
      setActionError(null);
      await reactivateMyVendorUser(email);
      setActionSuccess('User reactivated successfully');
      fetchUsers();
      setTimeout(() => setActionSuccess(null), 3000);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to reactivate user');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
        <AlertCircle className="w-4 h-4" />
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-gray-500">{users.length} team member(s)</p>
        <button
          onClick={fetchUsers}
          className="text-sm text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
        >
          <RefreshCw className="w-3 h-3" />
          Refresh
        </button>
      </div>

      {actionError && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle className="w-4 h-4" />
          {actionError}
        </div>
      )}

      {actionSuccess && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {actionSuccess}
        </div>
      )}

      {users.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>No team members yet</p>
          <p className="text-sm mt-1">Invite team members from the Invitations tab</p>
        </div>
      )}

      {users.length > 0 && (
        <div data-testid="vendor-admin-list-users" className="space-y-3">
          {users.map((vendorUser) => {
            const isCurrentUser = vendorUser.email.toLowerCase() === currentUserEmail?.toLowerCase();
            return (
              <div
                key={vendorUser.email}
                data-testid={`vendor-admin-item-user-${vendorUser.email.replace(/[^a-zA-Z0-9]/g, '-')}`}
                className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border rounded-lg ${
                  isCurrentUser ? 'border-gray-300 bg-gray-50/50' : 'border-gray-200'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                    <User className="w-5 h-5 text-gray-500" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900">
                        {vendorUser.firstName} {vendorUser.lastName}
                      </p>
                      {isCurrentUser && (
                        <span className="text-xs text-gray-500">(You)</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{vendorUser.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      vendorUser.vendorRole === 'admin'
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {vendorUser.vendorRole === 'admin' ? (
                      <Shield className="w-3 h-3" />
                    ) : (
                      <User className="w-3 h-3" />
                    )}
                    {vendorUser.vendorRole === 'admin' ? 'Admin' : 'User'}
                  </span>
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      vendorUser.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {vendorUser.isActive ? 'Active' : 'Inactive'}
                  </span>

                  {/* Actions - hidden for current user */}
                  {!isCurrentUser && (
                    <div className="flex items-center gap-1">
                      {vendorUser.isActive && (
                        <>
                          <button
                            onClick={() => handleToggleRole(vendorUser.email, vendorUser.vendorRole)}
                            data-testid={`vendor-admin-btn-toggle-role-${vendorUser.email.replace(/[^a-zA-Z0-9]/g, '-')}`}
                            className={`p-1.5 rounded transition-colors ${
                              vendorUser.vendorRole === 'admin'
                                ? 'text-orange-500 hover:text-orange-700 hover:bg-orange-50'
                                : 'text-purple-500 hover:text-purple-700 hover:bg-purple-50'
                            }`}
                            title={vendorUser.vendorRole === 'admin' ? 'Demote to User' : 'Promote to Admin'}
                          >
                            <UserCog className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeactivate(vendorUser.email)}
                            data-testid={`vendor-admin-btn-deactivate-${vendorUser.email.replace(/[^a-zA-Z0-9]/g, '-')}`}
                            className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                            title="Deactivate"
                          >
                            <UserMinus className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {!vendorUser.isActive && (
                        <button
                          onClick={() => handleReactivate(vendorUser.email)}
                          data-testid={`vendor-admin-btn-reactivate-${vendorUser.email.replace(/[^a-zA-Z0-9]/g, '-')}`}
                          className="p-1.5 text-green-500 hover:text-green-700 hover:bg-green-50 rounded transition-colors"
                          title="Reactivate"
                        >
                          <UserPlus className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ===== Invitations Tab =====
interface InvitationsTabProps {
  vendorName: string;
}

function InvitationsTab({ vendorName }: InvitationsTabProps) {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);

  const fetchInvitations = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getMyVendorInvitations();
      if (response.status) {
        setInvitations(response.data.filter((inv) => inv.status === 'pending'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load invitations');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  const handleResend = async (email: string) => {
    try {
      await resendMyVendorInvitation(email);
      alert('Invitation resent successfully');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to resend invitation');
    }
  };

  const handleCancel = async (email: string) => {
    if (!confirm(`Cancel invitation for ${email}?`)) return;
    try {
      await cancelMyVendorInvitation(email);
      fetchInvitations();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to cancel invitation');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-gray-500">{invitations.length} pending invitation(s)</p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={fetchInvitations}
            disabled={isLoading}
            className="text-sm text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
          >
            <RefreshCw className={`w-3 h-3 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowInviteModal(true)}
            className="flex items-center gap-2 px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700"
          >
            <Plus className="w-4 h-4" />
            Invite Team Member
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      )}

      {!isLoading && invitations.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Mail className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>No pending invitations</p>
          <p className="text-sm mt-1">Invite team members to join your organization</p>
        </div>
      )}

      {!isLoading && invitations.length > 0 && (
        <div className="space-y-3">
          {invitations.map((invitation) => (
            <div
              key={invitation.email}
              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border border-gray-200 rounded-lg"
            >
              <div>
                <p className="font-medium text-gray-900">{invitation.email}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
                      invitation.vendorRole === 'admin'
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {invitation.vendorRole === 'admin' ? 'Admin' : 'User'}
                  </span>
                  <span className="text-xs text-gray-400">
                    Expires {new Date(invitation.expiresAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleResend(invitation.email)}
                  className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded transition-colors"
                >
                  Resend
                </button>
                <button
                  onClick={() => handleCancel(invitation.email)}
                  className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Invite Modal */}
      <InviteModal
        isOpen={showInviteModal}
        vendorName={vendorName}
        onClose={() => setShowInviteModal(false)}
        onSuccess={() => {
          setShowInviteModal(false);
          fetchInvitations();
        }}
      />
    </div>
  );
}

// ===== Domains Tab =====
interface DomainsTabProps {
  vendorName: string;
}

function DomainsTab({ vendorName }: DomainsTabProps) {
  const [domains, setDomains] = useState<DomainWhitelist[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const fetchDomains = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getMyVendorDomains();
      if (response.status) {
        setDomains(response.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load domains');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDomains();
  }, [fetchDomains]);

  const handleRemove = async (domain: string) => {
    if (!confirm(`Remove @${domain} from whitelist? Users with this domain will no longer be able to self-register.`)) return;
    try {
      await removeMyVendorDomain(domain);
      fetchDomains();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to remove domain');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-gray-500">{domains.length} whitelisted domain(s)</p>
          <p className="text-xs text-gray-400 mt-1">
            Users with these email domains can sign up automatically
          </p>
        </div>
        <div className="flex flex-wrap gap-2 flex-shrink-0">
          <button
            onClick={fetchDomains}
            disabled={isLoading}
            className="text-sm text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
          >
            <RefreshCw className={`w-3 h-3 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700"
          >
            <Plus className="w-4 h-4" />
            Add Domain
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      )}

      {!isLoading && domains.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Globe className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>No whitelisted domains</p>
          <p className="text-sm mt-1">Add a domain to allow users to self-register</p>
        </div>
      )}

      {!isLoading && domains.length > 0 && (
        <div className="space-y-3">
          {domains.map((domain) => (
            <div
              key={domain.domain}
              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border border-gray-200 rounded-lg"
            >
              <div>
                <p className="font-medium text-gray-900">@{domain.domain}</p>
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs mt-1 ${
                    domain.defaultVendorRole === 'admin'
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  Default role: {domain.defaultVendorRole === 'admin' ? 'Admin' : 'User'}
                </span>
              </div>
              <button
                onClick={() => handleRemove(domain.domain)}
                className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                title="Remove domain"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add Domain Modal */}
      <AddDomainModal
        isOpen={showAddModal}
        vendorName={vendorName}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          setShowAddModal(false);
          fetchDomains();
        }}
      />
    </div>
  );
}

// ===== Invite Modal =====
interface InviteModalProps {
  isOpen: boolean;
  vendorName: string;
  onClose: () => void;
  onSuccess: () => void;
}

function InviteModal({ isOpen, vendorName, onClose, onSuccess }: InviteModalProps) {
  const [email, setEmail] = useState('');
  const [vendorRole, setVendorRole] = useState<VendorUserRole>('user');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setVendorRole('user');
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter an email address');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      await inviteToMyVendor(email, vendorRole);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Invite Team Member to ${vendorName}`}
      size="md"
      footer={
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 py-2.5 px-4 border border-gray-200 rounded-lg text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="invite-form"
            disabled={isSubmitting}
            className="flex-1 py-2.5 px-4 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Send Invitation
          </button>
        </div>
      }
    >
      <form id="invite-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="colleague@example.com"
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="vendorRole"
                value="user"
                checked={vendorRole === 'user'}
                onChange={() => setVendorRole('user')}
                className="w-4 h-4 text-primary-600"
              />
              <User className="w-4 h-4 text-gray-500" />
              <span className="text-sm">User</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="vendorRole"
                value="admin"
                checked={vendorRole === 'admin'}
                onChange={() => setVendorRole('admin')}
                className="w-4 h-4 text-primary-600"
              />
              <Shield className="w-4 h-4 text-purple-500" />
              <span className="text-sm">Admin</span>
            </label>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Admins can manage team members, invitations, and domain settings.
          </p>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}

// ===== Add Domain Modal =====
interface AddDomainModalProps {
  isOpen: boolean;
  vendorName: string;
  onClose: () => void;
  onSuccess: () => void;
}

function AddDomainModal({ isOpen, vendorName, onClose, onSuccess }: AddDomainModalProps) {
  const [domain, setDomain] = useState('');
  const [defaultVendorRole, setDefaultVendorRole] = useState<VendorUserRole>('user');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setDomain('');
      setDefaultVendorRole('user');
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanDomain = domain.replace('@', '').toLowerCase().trim();

    if (!cleanDomain) {
      setError('Please enter a domain');
      return;
    }

    // Check for blocked generic domains
    if (BLOCKED_DOMAINS.includes(cleanDomain)) {
      setError(
        `Generic email domains like ${cleanDomain} are not allowed. Please use a company domain.`
      );
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      await addMyVendorDomain(cleanDomain, defaultVendorRole);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add domain');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Add Domain to ${vendorName}`}
      size="md"
      footer={
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 py-2.5 px-4 border border-gray-200 rounded-lg text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="add-domain-form"
            disabled={isSubmitting}
            className="flex-1 py-2.5 px-4 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Add Domain
          </button>
        </div>
      }
    >
      <form id="add-domain-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Domain</label>
          <div className="flex items-center">
            <span className="px-3 py-2.5 bg-gray-100 border border-r-0 border-gray-200 rounded-l-lg text-gray-500">
              @
            </span>
            <input
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value.replace('@', ''))}
              placeholder="company.com"
              className="flex-1 px-3 py-2.5 border border-gray-200 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
              required
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">
            All users with this email domain can request access
          </p>
          <p className="mt-1 text-xs text-orange-600">
            Generic domains (gmail.com, hotmail.com, etc.) are not allowed
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Default Role</label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="defaultVendorRole"
                value="user"
                checked={defaultVendorRole === 'user'}
                onChange={() => setDefaultVendorRole('user')}
                className="w-4 h-4 text-primary-600"
              />
              <User className="w-4 h-4 text-gray-500" />
              <span className="text-sm">User</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="defaultVendorRole"
                value="admin"
                checked={defaultVendorRole === 'admin'}
                onChange={() => setDefaultVendorRole('admin')}
                className="w-4 h-4 text-primary-600"
              />
              <Shield className="w-4 h-4 text-purple-500" />
              <span className="text-sm">Admin</span>
            </label>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Users who sign up with this domain will be assigned this role by default.
          </p>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}

export default VendorAdminPage;
